// THREADS SEWING MACHINE panel — a craft-station menu of the employment-gated sewing recipes (clothing
// from processed leather). Mirrors the cooking pot look: input → output, a quantity stepper, a Sew
// button that starts the craft channel. Only reachable while clocked in + standing at the machine
// (world3d gates it).

import { SEW_RECIPES, ITEM_NAMES, type CookRecipe } from "@ghostland/shared";
import { injectTheme } from "./theme.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { itemThumb } from "../items/itemThumb.js";

export interface SewActions {
  /** Start sewing `quantity` units of `recipeId` at the machine (server-authorised, per-unit channel). */
  sew: (recipeId: string, quantity: number) => void;
}
let actions: SewActions | null = null;
export function setSewActions(a: SewActions): void { actions = a; }

const STYLE_ID = "ghostland-sew-style";
const CSS = `
#gl-sew-overlay { position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(5,0,7,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text); }
#gl-sew-overlay.open { display: flex; }
#gl-sew-card { width: 400px; max-width: 92vw; max-height: 82vh; display: flex; flex-direction: column;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7); overflow: hidden; }
#gl-sew-head { padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border); }
#gl-sew-head h1 { margin: 0; font-size: 15px; letter-spacing: .16em; }
#gl-sew-head .hint { font-size: 11px; color: var(--gl-muted); margin-top: 4px; }
#gl-sew-body { padding: 14px 18px; overflow-y: auto; }
.gl-sew-recipe { border: 1px solid var(--gl-border); border-radius: 9px; padding: 12px 14px; margin-bottom: 12px; background: var(--gl-panel-2); }
.gl-sew-title { font-size: 13px; margin-bottom: 4px; }
.gl-sew-title .t { font-size: 10px; color: var(--gl-muted); margin-left: 6px; }
.gl-sew-io { display: flex; align-items: center; gap: 10px; margin: 6px 0 12px; flex-wrap: wrap; }
.gl-sew-io .chip { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.gl-sew-io .chip canvas { width: 30px; height: 30px; image-rendering: pixelated; }
.gl-sew-io .chip .lack { color: #e06a6a; }
.gl-sew-io .arrow { color: var(--gl-muted); }
.gl-sew-qty { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 12px; }
.gl-sew-qty input { width: 60px; padding: 6px 8px; box-sizing: border-box; text-align: center;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 6px; color: var(--gl-text); font-family: inherit; font-size: 13px; }
.gl-sew-qty button { width: 26px; height: 26px; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-text); cursor: pointer; font-family: inherit; font-size: 14px; }
.gl-sew-qty button:hover { border-color: var(--gl-border-focus); }
.gl-sew-qty .max { color: var(--gl-muted); }
.gl-sew-go { width: 100%; padding: 9px 0; background: var(--gl-accent); color: var(--gl-accent-fg);
  border: 1px solid var(--gl-accent); border-radius: 7px; cursor: pointer; font-family: inherit; font-size: 13px; }
.gl-sew-go:hover:not(:disabled) { background: var(--gl-accent-hover); border-color: var(--gl-accent-hover); }
.gl-sew-go:disabled { opacity: .45; cursor: default; }
#gl-sew-close { margin: 0 18px 16px; padding: 7px 0; background: none; border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let bodyEl: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];

function ownedCount(itemId: string): number {
  return inventoryStore.get().reduce((n, s) => (s.itemId === itemId ? n + s.quantity : n), 0);
}
function affordable(recipe: CookRecipe): number {
  let min = Infinity;
  for (const ing of recipe.inputs) min = Math.min(min, Math.floor(ownedCount(ing.itemId) / ing.qty));
  return Number.isFinite(min) ? min : 0;
}
function iconFor(itemId: string): HTMLCanvasElement {
  const c = itemThumb(itemId); c.style.imageRendering = "pixelated"; return c; // real garment art for clothes
}

/** One recipe card: inputs→output, a quantity stepper, and a Sew button. */
function recipeCard(recipe: CookRecipe): HTMLDivElement {
  const max = affordable(recipe);
  const card = document.createElement("div"); card.className = "gl-sew-recipe";
  const secs = Math.round(recipe.durationMs / 1000);
  card.innerHTML = `<div class="gl-sew-title">${recipe.name} <span class="t">${secs}s each</span></div>
    <div class="gl-sew-io"></div>
    <div class="gl-sew-qty">Qty
      <button class="gl-sew-minus">−</button>
      <input class="gl-sew-q" type="number" min="1" value="1" />
      <button class="gl-sew-plus">+</button>
      <span class="max">/ ${max} max</span>
    </div>
    <button class="gl-sew-go">Sew</button>`;

  const io = card.querySelector(".gl-sew-io")!;
  recipe.inputs.forEach((ing, i) => {
    if (i > 0) { const plus = document.createElement("span"); plus.className = "arrow"; plus.textContent = "+"; io.appendChild(plus); }
    const chip = document.createElement("div"); chip.className = "chip";
    chip.appendChild(iconFor(ing.itemId));
    const have = ownedCount(ing.itemId);
    const lab = document.createElement("span"); lab.className = have < ing.qty ? "lack" : "";
    lab.textContent = `${ITEM_NAMES[ing.itemId as keyof typeof ITEM_NAMES] ?? ing.itemId} ${have}/${ing.qty}`;
    chip.appendChild(lab); io.appendChild(chip);
  });
  const arrow = document.createElement("span"); arrow.className = "arrow"; arrow.textContent = "→"; io.appendChild(arrow);
  const outChip = document.createElement("div"); outChip.className = "chip";
  outChip.appendChild(iconFor(recipe.output.itemId));
  const outLab = document.createElement("span");
  outLab.textContent = ITEM_NAMES[recipe.output.itemId as keyof typeof ITEM_NAMES] ?? recipe.output.itemId;
  outChip.appendChild(outLab); io.appendChild(outChip);

  const input = card.querySelector<HTMLInputElement>(".gl-sew-q")!;
  const go = card.querySelector<HTMLButtonElement>(".gl-sew-go")!;
  const clamp = (): number => { let v = Math.floor(Number(input.value) || 1); v = Math.max(1, Math.min(Math.max(1, max), v)); input.value = String(v); return v; };
  card.querySelector(".gl-sew-minus")!.addEventListener("click", () => { input.value = String(Math.max(1, (Math.floor(Number(input.value) || 1)) - 1)); clamp(); });
  card.querySelector(".gl-sew-plus")!.addEventListener("click", () => { input.value = String((Math.floor(Number(input.value) || 1)) + 1); clamp(); });
  input.addEventListener("change", clamp);
  if (max < 1) { go.disabled = true; go.textContent = `Need ${recipe.inputs.map((g) => `${g.qty} ${ITEM_NAMES[g.itemId as keyof typeof ITEM_NAMES] ?? g.itemId}`).join(" + ")}`; }
  go.addEventListener("click", () => { if (go.disabled) return; const qty = clamp(); actions?.sew(recipe.id, qty); closeSew(); });
  return card;
}

function render(): void {
  if (!bodyEl) return;
  bodyEl.innerHTML = "";
  for (const recipe of SEW_RECIPES) bodyEl.appendChild(recipeCard(recipe));
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) { const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s); }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-sew-overlay";
  overlay.innerHTML = `<div id="gl-sew-card">
    <div id="gl-sew-head"><h1>SEWING MACHINE</h1><div class="hint">Stitch garments from processed leather. Stand still while it works.</div></div>
    <div id="gl-sew-body"></div>
    <button id="gl-sew-close">Close (Esc)</button>
  </div>`;
  document.body.appendChild(overlay);
  bodyEl = overlay.querySelector("#gl-sew-body");
  overlay.querySelector("#gl-sew-close")!.addEventListener("click", closeSew);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeSew(); });
}

export function openSew(): void {
  ensure();
  render();
  unsub = [inventoryStore.subscribe(render)];
  overlay!.classList.add("open");
}
export function closeSew(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}
export function isSewOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
